import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { CartContext } from './CartContext';
import Navbar from './components/Navbar';
import LoginPage from './components/LoginPage';
import RegistrationPage from './components/RegistrationPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import ProductListPage from './components/ProductListPage';
import ShoppingCartPage from './components/ShoppingCartPage';
import OrderPage from './components/OrderPage';
import ForgotPasswordEmailForm from './components/ForgotPasswordEmailForm';
import OrderConfirmationPage from './components/OrderConfirmationPage';
import HomePage from './components/HomePage';

export default function App() {
  const [page, setPage] = useState(() => {
    const stored = localStorage.getItem('page');
    try {
      return stored ? JSON.parse(stored) : 'home';
    } catch {
      return stored || 'home';
    }
  });

  const [user, setUser] = useState(null);
  useEffect(() => {
  const userName = localStorage.getItem('userName');
  const customer = localStorage.getItem('customer');
  const cusAddress = localStorage.getItem('cusAddress');
  const mail = localStorage.getItem('mail');

  if (userName && customer) {
    setUser({
      name: userName,
      customer: customer,
      address: cusAddress || ''
    });
  }

  const bankName = localStorage.getItem('bankName');
  if (bankName) {
    setBank({
      accno: localStorage.getItem('accno') || '',
      bankName,
      bid: parseInt(localStorage.getItem('bid')) || 0,
      branch: localStorage.getItem('branch') || '',
      ifscode: localStorage.getItem('ifscode') || ''
    });
  }

  const qrcodelink = localStorage.getItem('qrcodelink');
  if (qrcodelink) {
    setQr({ qrcodelink });
  }
}, []);

  const [bank, setBank] = useState(null);
  const [qr, setQr] = useState(null);
  const [cart, setCart] = useState([]);
  const [userEmail, setUserEmail] = useState('');


  useEffect(() => {
    localStorage.setItem('page', typeof page === 'string' ? page : JSON.stringify(page));
  }, [page]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  }, [removeFromCart]);

  const clearCart = () => setCart([]);

  const cartContextValue = useMemo(() => ({
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    itemCount: cart.length,
    totalQuantity: cart.reduce((sum, item) => sum + item.quantity, 0),
    totalPrice: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
  }), [cart, addToCart, updateQuantity, removeFromCart]);

  const handleRegister = (newUser) => {
    setUserEmail(newUser.email);
    setPage('verify-email');
  };

  const handleLogin = async (credentials, setError, setLoading) => {
    setLoading(true);
    setError('');
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (!ipResponse.ok) throw new Error('Could not fetch IP address.');
      const ipData = await ipResponse.json();
      const ipAddress = ipData.ip;

      const appKey = localStorage.getItem('appKey');
      const sessionId = '9944010155';
      const comId = 1;
      const { username, password } = credentials;

      const params = new URLSearchParams({
        UsrName: username,
        UsrPwd: password,
        SessionId: sessionId,
        IPAddress: ipAddress,
        ComId: comId,
        AppKey: appKey
      });

      const apiUrl = `http://kngservice.revampapps.com/CheckCustomerLogin?${params.toString()}`;

      const loginResponse = await fetch(apiUrl);
      if (!loginResponse.ok) {
        throw new Error(`API Error: ${loginResponse.status} - ${loginResponse.statusText}`);
      }

      const loginData = await loginResponse.json();
      if (loginData?.CheckCustomerLoginjs?.Table?.length > 0) {
        const userInfo = loginData.CheckCustomerLoginjs.Table[0];
        const BankInfo = loginData.CheckCustomerLoginjs.Table1?.[0] || {};
        const QrInfo = loginData.CheckCustomerLoginjs.Table2?.[0] || {};

        setUser({
          name: userInfo.UsrName || username,
          customer: userInfo.Customer || '',
          address: userInfo.CusAddress || ''
        });

        setBank({
          accno: BankInfo.AccNo || '',
          bankName: BankInfo.BankName || '',
          bid: BankInfo.Bid || 0,
          branch: BankInfo.Branch || '',
          ifscode: BankInfo.IFSCCode || ''
        });

        setQr({ qrcodelink: QrInfo.QRUPILINK || '' });

        localStorage.setItem('userName', userInfo.UsrName || username);
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('cusAddress', userInfo.CusAddress || '');
        localStorage.setItem('customer', userInfo.Customer || '');
        localStorage.setItem('vname', userInfo.Customer || '');
        localStorage.setItem('vid', userInfo.Usrid || '0');
        localStorage.setItem('vcode', userInfo.UsrName || '');
        localStorage.setItem('mail', userInfo.mail || '');

        localStorage.setItem('accno', BankInfo.AccNo || '');
        localStorage.setItem('bankName', BankInfo.BankName || '');
        localStorage.setItem('bid', BankInfo.Bid || '0');
        localStorage.setItem('branch', BankInfo.Branch || '');
        localStorage.setItem('ifscode', BankInfo.IFSCCode || '');
        localStorage.setItem('qrcodelink', QrInfo.QRUPILINK || '');

        setPage('products');
      } else {
        throw new Error('Invalid username or password.');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setCart([]);
    localStorage.removeItem('page'); // ❗ Clear stored page on logout
    setPage('login');
  };

  const handleCompleteOrder = () => {
    console.log('Order Completed:', { user, cart, total: cartContextValue.totalPrice });
    clearCart();
    setPage('confirmation');
  };

 const renderPage = () => {
    if (typeof page === 'object' && page.name === 'forgot') {
      if (!page.email) {
        return <ForgotPasswordEmailForm setPage={setPage} />;
      }
      return <EmailVerificationPage email={page.email} setPage={setPage} mode="reset" />;
    }

    switch (page) {
      case 'home':
        return <HomePage setPage={setPage} />;
      case 'register':
        return <RegistrationPage onRegister={handleRegister} setPage={setPage} />;
      case 'verify-email':
        return <EmailVerificationPage email={userEmail} setPage={setPage} />;
      case 'products':
        return <ProductListPage user={user} />;
      case 'cart':
        return <ShoppingCartPage setPage={setPage} />;
      case 'order':
        return <OrderPage setPage={setPage} onCompleteOrder={handleCompleteOrder} />;
      case 'confirmation':
        return <OrderConfirmationPage setPage={setPage} />;
      case 'login':
      default:
        return <LoginPage onLogin={handleLogin} setPage={setPage} />;
    }
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      <div className="bg-gray-50 min-h-screen font-sans text-gray-800">
        <Navbar user={user} setPage={setPage} onLogout={handleLogout} />
        <main className="p-4 sm:p-6 lg:p-8">
          {renderPage()}
        </main>
      </div>
    </CartContext.Provider>
  );
}
